/**
 * Where to buy something — as a link, not as an integration.
 *
 * A shopping list is only half a job. The other half is done in one of a handful
 * of apps that are already on the phone, and the fastest thing this app can do is
 * hand the name over to one of them with the search already typed.
 *
 * **These are plain https URLs on purpose, and they ARE the deep links.** Both
 * platforms let an app claim its own web addresses — Android through
 * `/.well-known/assetlinks.json`, iOS through
 * `/.well-known/apple-app-site-association` — and every store below claims the
 * search path used here, verified by reading those files:
 *
 *   Blinkit    com.grofers.customerapp            iOS `/s/*`
 *   Zepto      com.zeptoconsumerapp               iOS `*`
 *   Instamart  in.swiggy.android                  iOS `/instamart/*`
 *   BigBasket  com.bigbasket.mobileapp            iOS — search path NOT claimed
 *   JioMart    com.jpl.jiomart                    iOS `/products*`
 *   DMart      in.dmart                           iOS — only `/product/*`
 *   Flipkart   com.flipkart.android               iOS `*`
 *   Amazon     in.amazon.mShop.android.shopping   iOS `/s`
 *
 * So the app opens when it is installed, and the mobile site opens when it is
 * not. A custom scheme (`zepto://…`) would be the obvious alternative and is
 * strictly worse: it cannot be feature-detected from a browser, and when the
 * app is missing it dead-ends instead of degrading. See ADR 0023.
 *
 * THE LAST THREE ARE WEAKER, and it is written down rather than smoothed over.
 * All three open their Android app — the platform this product is built for
 * first — but BigBasket and DMart do not claim a search path on iOS, and the
 * JioMart and DMart web pages ask for a pincode before they show anything. They
 * earn their place anyway because they turn up constantly in real results, and
 * a card for a seller we cannot name or draw is worse than one we can.
 *
 * Ordered quick-commerce first, then the marketplaces. That is the only
 * difference between them that changes what you tap — minutes versus tomorrow —
 * and position carries it without spending a word.
 */
/**
 * The shops, in tap order.
 *
 * `encodeURIComponent` and not a `URLSearchParams` builder, because the query
 * is the ONLY variable part of any of these — a builder would let a caller
 * rearrange a URL that was verified as a whole against a live site.
 */
export const STORES = [
    {
        id: "blinkit",
        label: "Blinkit",
        href: (q) => `https://blinkit.com/s/?q=${encodeURIComponent(q)}`,
    },
    {
        id: "zepto",
        label: "Zepto",
        // zepto.com, not zeptonow.com: the old domain still works and still opens
        // the app, but it 301s, and a redirect is a second chance to lose the app
        // hand-off.
        href: (q) => `https://www.zepto.com/search?query=${encodeURIComponent(q)}`,
    },
    {
        id: "instamart",
        label: "Instamart",
        // `custom_back=true` is Swiggy's own canonical form — the bare `?query=`
        // redirects to it.
        href: (q) => `https://www.swiggy.com/instamart/search?custom_back=true&query=${encodeURIComponent(q)}`,
    },
    {
        id: "bigbasket",
        label: "BigBasket",
        href: (q) => `https://www.bigbasket.com/ps/?q=${encodeURIComponent(q)}`,
    },
    {
        id: "jiomart",
        label: "JioMart",
        // `/products?q=`: the search page that actually renders a result list.
        // `/catalogsearch/result?q=` was used first because it was the only search
        // path JioMart's iOS declaration claimed, but on the web it now shows an
        // empty page for a real product name — and the declaration has since
        // added `/products*` too (checked 2026-08-23), so nothing is lost.
        href: (q) => `https://www.jiomart.com/products?q=${encodeURIComponent(q)}`,
    },
    {
        id: "dmart",
        // "DMart", not "DMart Ready": the longer name wraps onto two lines in a
        // half-width tile and makes that one row taller than the seven beside it.
        // Google's listings say "DMart Ready"; `sellerName` prefers ours.
        label: "DMart",
        // The other two shapes both 404. This one is a live route.
        href: (q) => `https://www.dmart.in/search?searchTerm=${encodeURIComponent(q)}`,
    },
    {
        id: "flipkart",
        label: "Flipkart",
        // The marketplace search, not the grocery storefront: the grocery one needs
        // a delivery location set before it will show anything at all, so a cold
        // tap lands on "select delivery location" instead of on atta.
        href: (q) => `https://www.flipkart.com/search?q=${encodeURIComponent(q)}`,
    },
    {
        id: "amazon",
        label: "Amazon",
        href: (q) => `https://www.amazon.in/s?k=${encodeURIComponent(q)}`,
    },
];
/**
 * What to type into a shop's search box.
 *
 * The brand goes in front, because "Amul" narrows a milk search from a hundred
 * results to five — but only when the name has not already got it, which it
 * usually has: people write "Amul milk", and "Amul Amul milk" finds nothing.
 *
 * The size is deliberately left out even when we know it. A shop's search is
 * matching a catalogue, and "1 L" is the part most likely to be written
 * differently there ("1 ltr", "1000 ml") — narrowing on it turns a good result
 * list into an empty one.
 */
export function searchQuery({ name, brand }) {
    const what = name.replace(/\s+/g, " ").trim();
    if (!what)
        return "";
    const who = (brand ?? "").replace(/\s+/g, " ").trim();
    if (!who)
        return what;
    return hasWords(what, who) ? what : `${who} ${what}`;
}
/**
 * The buttons, ready to render.
 *
 * Empty when there is nothing to search for, so a blank name produces no
 * buttons rather than a row of links to shop homepages.
 */
export function storeLinks(what) {
    const q = searchQuery(what);
    if (!q)
        return [];
    return STORES.map((s) => ({ id: s.id, label: s.label, href: s.href(q) }));
}
/**
 * Does `haystack` already contain every word of `needle`?
 *
 * Whole words, case-insensitively: "Amul" must match "amul milk" but "mul"
 * must not, and a brand that is two words ("Mother Dairy") only counts as
 * present when both of them are.
 */
function hasWords(haystack, needle) {
    const words = new Set(haystack.toLowerCase().split(/\s+/).filter(Boolean));
    return needle
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .every((w) => words.has(w));
}
//# sourceMappingURL=stores.js.map